// client/src/api/services/operatorKitService.ts
import { apiClient } from '../client';
import { ENDPOINTS, buildQueryString } from '../endpoints';
import { AxiosError } from 'axios';

// ==================== TYPES ====================
export interface DashboardStats {
  totalUsers: number;
  totalKits?: number;
  pendingKits?: number;
  activeKits: number;
  networkStatus: string;
  locations: number;
}

export interface RecentUser {
  id: string;
  name: string;
  email: string;
  location: string;
  status: 'active' | 'pending' | 'inactive';
  joinedDate: string;
}

export interface Kit {
  id: string;
  _id?: string;
  name: string;
  status: 'active' | 'pending' | 'disabled';
  activationDate?: string;
  createdAt?: string; // ✅ ADDED
  updatedAt?: string; // ✅ ADDED
  location: string | { 
    address?: string; 
    city?: string;
    state?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    }
  };
  deploymentLocation?: string; // ✅ ADDED
  deviceBrand?: string;
  deviceModel?: string;
  macAddress?: string;
  serialNumber?: string;
  networkCoverageRadius?: number;
  coordinates?: {
    lat: number;
    lng: number;
  };
  kitType?: 'basic' | 'advanced' | 'premium'; // ✅ FIXED: Changed 'advance' to 'advanced'
  quantity?: number;
  kycStatus?: string;
  paymentStatus?: string;
}

export interface KitListResponse {
  success: boolean;
  kits: Kit[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface AddKitData {
  kitType: 'basic' | 'advanced' | 'premium'; // ✅ FIXED: Changed 'advance' to 'advanced'
  quantity: number;
  deploymentLocation: string;
}

export interface AddKitResponse {
  success: boolean;
  message: string;
  kitId: string;
  kit: {
    id: string;
    name: string;
    status: string;
    kitType: 'basic' | 'advanced' | 'premium'; // ✅ FIXED
    quantity: number;
    deploymentLocation: string;
    location?: unknown; // ✅ ADDED
  };
}

// ✅ FIXED: Update interface to match backend
export interface UpdateKitData {
  name?: string;
  kitType?: 'basic' | 'advanced' | 'premium'; // ✅ FIXED: Changed 'advance' to 'advanced'
  quantity?: number;
  deploymentLocation?: string;
  status?: 'active' | 'pending' | 'disabled' | 'maintenance';
  deviceBrand?: string;
  deviceModel?: string;
  networkCoverageRadius?: number;
  location?: {
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
}

// ==================== OPERATOR KIT SERVICE ====================
const operatorKitService = {
  // Get dashboard stats
  getDashboard: async (): Promise<DashboardStats> => {
    try {
      const response = await apiClient.get(
        ENDPOINTS.OPERATOR_KIT.DASHBOARD
      );
      
      console.log('📊 Raw dashboard response:', response.data);
      
      // Handle different response formats
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      // If response has stats directly
      if (response.data.stats) {
        return response.data.stats;
      }
      
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Dashboard API error:', error);
      throw error;
    }
  },

  // Get recent users
  getRecentUsers: async (): Promise<RecentUser[]> => {
    try {
      const response = await apiClient.get(
        ENDPOINTS.OPERATOR_KIT.RECENT_USERS
      );
      
      console.log('👥 Raw recent users response:', response.data);
      
      // Handle different response formats
      if (response.data.success && response.data.users) {
        return response.data.users;
      }
      
      // If response has data field
      if (response.data.data) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Recent users API error:', error);
      throw error;
    }
  },

  // Get all kits with filters
  getAllKits: async (params?: {
    status?: 'all' | 'active' | 'pending' | 'disabled';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<KitListResponse> => {
    try {
      const queryString = params ? buildQueryString(params) : '';
      console.log('📡 Calling getAllKits API:', `${ENDPOINTS.OPERATOR_KIT.ALL_KITS}${queryString}`);
      
      const response = await apiClient.get(
        `${ENDPOINTS.OPERATOR_KIT.ALL_KITS}${queryString}`
      );
      
      console.log('📡 getAllKits raw response:', response.data);
      
      // Handle different response formats
      if (response.data.success && response.data.kits) {
        return response.data;
      }
      
      // If response has data field
      if (response.data.data) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error: unknown) {
      console.error('❌ getAllKits API error:', error);
      throw error;
    }
  },

  // Get single kit details
  getKitById: async (kitId: string): Promise<Kit> => {
    try {
      console.log('🔍 Fetching kit by ID:', kitId);
      
      const response = await apiClient.get<{ success: boolean; kit: Kit }>(
        ENDPOINTS.OPERATOR_KIT.KIT_DETAILS(kitId)
      );
      
      console.log('✅ Kit details received:', response.data.kit);
      
      return response.data.kit;
    } catch (error: unknown) {
      console.error('❌ getKitById error:', error);
      throw error;
    }
  },

  // Add new kit
  addKit: async (data: AddKitData): Promise<AddKitResponse> => {
    try {
      console.log('➕ Adding kit:', data);
      
      const response = await apiClient.post<AddKitResponse>(
        ENDPOINTS.OPERATOR_KIT.ADD_KIT,
        data
      );
      
      console.log('✅ Kit added:', response.data);
      
      return response.data;
    } catch (error: unknown) {
      console.error('❌ addKit error:', error);
      throw error;
    }
  },

  // ✅ FIXED: Update kit with proper typing
  updateKit: async (kitId: string, data: UpdateKitData) => {
    try {
      console.log('🔄 Updating kit:', kitId);
      console.log('📤 Update payload:', data);
      
      // ✅ VALIDATE: Ensure kitType is correct if provided
      if (data.kitType) {
        const validKitTypes: Array<'basic' | 'advanced' | 'premium'> = ['basic', 'advanced', 'premium'];
        if (!validKitTypes.includes(data.kitType)) {
          throw new Error(`Invalid kit type: ${data.kitType}. Must be 'basic', 'advanced', or 'premium'`);
        }
      }
      
      const response = await apiClient.put(
        ENDPOINTS.OPERATOR_KIT.UPDATE_KIT(kitId),
        data
      );
      
      console.log('✅ Update response:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ Update kit error:', error);
      if (error instanceof AxiosError) {
        console.error('❌ Error response:', error.response?.data);
      }
      throw error;
    }
  },

  // Delete kit
  deleteKit: async (kitId: string) => {
    try {
      console.log('🗑️ Deleting kit:', kitId);
      
      const response = await apiClient.delete(
        ENDPOINTS.OPERATOR_KIT.DELETE_KIT(kitId)
      );
      
      console.log('✅ Kit deleted:', response.data);
      
      return response.data;
    } catch (error: unknown) {
      console.error('❌ deleteKit error:', error);
      throw error;
    }
  },

  // Search kits
  searchKits: async (query: string) => {
    try {
      console.log('🔍 Searching kits:', query);
      
      const response = await apiClient.get(
        `${ENDPOINTS.OPERATOR_KIT.SEARCH_KITS}?q=${encodeURIComponent(query)}`
      );
      
      console.log('✅ Search results:', response.data);
      
      return response.data;
    } catch (error: unknown) {
      console.error('❌ searchKits error:', error);
      throw error;
    }
  },
};

export default operatorKitService;